import React, { useMemo } from 'react'
import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'

import {
  TtButton,
  TtDropdown,
  TtIcon,
  TtMenu,
  TtMenuItem,
  TtMenuLabel,
  TtTrayPoll,
} from '@23/ui-react'

import {
  setActiveCard,
  updatePollCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../../firebase/hooks/useTray'
import { ChyronType } from '../../../../../firebase/model/chyron-model'
import {
  removeOverlay,
  useOverlayState,
} from '../../../../../firebase/hooks/useOverlays'
import { useConnectInfoQuery } from '../../../../../global/blocs/useConnectInfo'
import { type PropsWithTrayItemChildren } from '../../TrayItem'
import usePollsOverlays from '../../../../hooks/usePollsOverlay'
import { usePollsQuery } from '../../hooks/use-polls'
import { setTrayCardEditing, useTrayCardEditingId } from '../../useTrayEditing'

import { PollForm } from './Content/PollForm'
import { CreatedPoll } from './Content/CreatedPoll'
import { StartedPoll } from './Content/StartedPoll'
import { ClosedPoll } from './Content/ClosedPoll'

import './PollCard.scss'

interface PollCardNewProps {
  id: string
}

const PollCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<PollCardNewProps>) => {
  const { data: webinarInfo } = useConnectInfoQuery()
  const card = useTrayCard(id)
  const cardState = useTrayCardState(id)
  const overlay = useOverlayState()
  const { togglePollOverlay } = usePollsOverlays()

  const cardBeingEdited = useTrayCardEditingId()
  const clearEditing = () => setTrayCardEditing(null)

  const isCurrentlyEditing = cardBeingEdited === id

  const { data: polls } = usePollsQuery()

  const isChyronShowing =
    overlay?.type === ChyronType.PollResult &&
    card?.type === 'poll' &&
    overlay.id === card.poll?.id.toString()

  const showPollTvCard = () => {
    if (card?.type === 'poll' && card.poll) {
      const totalAnswers = card.poll.options.reduce(
        (total, o) => total + o.count,
        0
      )
      if (!isChyronShowing) {
        togglePollOverlay({
          poll_id: card.poll.id.toString(),
          question: card.poll.question,
          opened_date: card.poll.startedAt,
          open: Number(card.poll.started),
          options: card.poll.options.map((option) => ({
            option: option.option,
            poll_option_id: option.id.toString(),
            answer_percentage:
              totalAnswers === 0
                ? '0'
                : (option.count / totalAnswers).toFixed(2),
          })),
        })
      }
    }
  }

  const onCardClickHandler = () => {
    if (!card) return

    setActiveCard(card.id)
    if (card.type === 'poll' && card.poll?.startedAt) showPollTvCard()
  }

  const state = useMemo(() => {
    if (card?.type !== 'poll') return undefined

    if (!card.poll) {
      return 'empty'
    }

    if (card.poll.startedAt) {
      if (card.poll.started) {
        return 'started'
      }
      return 'closed'
    }

    return 'created'
  }, [card])

  const renderBody = () => {
    switch (state) {
      case 'empty': {
        return <PollForm cardId={id} />
      }
      case 'created': {
        if (isCurrentlyEditing) {
          return (
            <PollForm
              cardId={id}
              // @ts-expect-error — we know the card will have poll property
              poll={card.poll}
              onSubmit={clearEditing}
              onCancel={clearEditing}
            />
          )
        }

        // @ts-expect-error — we know the card will have poll property
        return <CreatedPoll poll={card.poll} />
      }
      case 'started': {
        // @ts-expect-error — we know the card will have poll property
        return <StartedPoll poll={card.poll} />
      }
      case 'closed': {
        // @ts-expect-error — we know the card will have poll property
        return <ClosedPoll poll={card.poll} />
      }
      default:
        return null
    }
  }

  const onPollSelectHandler = (pollId: number) => {
    const poll = polls?.find((p) => p.id === pollId)
    if (poll) {
      const { isDisplayingResults, ...pollWithoutDisplay } = poll
      updatePollCard(id, pollWithoutDisplay)
    }
  }

  if (!card || card.type !== 'poll' || state === undefined) return null

  const isWebinarGearMode = webinarInfo?.streaming_method === 'hardware'

  return (
    <TtTrayPoll
      subject={intl.formatMessage({ defaultMessage: 'Poll' })}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      onTtClick={(event) => {
        if (event.target === event.currentTarget) {
          onCardClickHandler()
        }
      }}
    >
      {state === 'empty' && polls && polls.length > 0 && (
        <TtDropdown slot="toolbar" placement="bottom-start" closeOnSelect hoist>
          <TtButton
            slot="trigger"
            label={intl.formatMessage({ defaultMessage: 'Select poll' })}
            color="secondary"
            variant="ghost"
            size="x-small"
            circle
          >
            <TtIcon library="one/webinars" name="polls" />
          </TtButton>
          <TtMenu
            onTtSelect={(e) =>
              onPollSelectHandler(parseInt(e.detail.item.value, 10))
            }
          >
            <TtMenuLabel>
              <FormattedMessage defaultMessage="Choose existing poll" />
            </TtMenuLabel>
            {polls.map((poll) => (
              <TtMenuItem
                className="menu-existing-poll-selector-item"
                key={poll.id}
                value={poll.id.toString()}
              >
                {poll.question}
              </TtMenuItem>
            ))}
          </TtMenu>
        </TtDropdown>
      )}
      {(state === 'started' || state === 'closed') && !isWebinarGearMode && (
        <TtButton
          slot="toolbar"
          label={
            isChyronShowing
              ? intl.formatMessage({ defaultMessage: 'Hide Poll TvCard' })
              : intl.formatMessage({ defaultMessage: 'Show Poll TvCard' })
          }
          color="secondary"
          variant="ghost"
          size="x-small"
          circle
          onClick={isChyronShowing ? removeOverlay : showPollTvCard}
        >
          <TtIcon
            library="one/webinars"
            name={isChyronShowing ? 'visibility-off' : 'visibility-on'}
          />
        </TtButton>
      )}
      {children?.({
        canEdit: !isCurrentlyEditing && state === 'created',
      })}
      {renderBody()}
    </TtTrayPoll>
  )
}

export { PollCard }
